import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Loader2, Table2, RefreshCw, Save } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LABELS = {
  store_name: 'Store *', week_ending: 'Week ending', net_sales: 'Net sales *', net_sales_ly: 'Net sales last year',
  labor_percent: 'Labour %', food_percent: 'Food cost %', paper_percent: 'Paper %', oil_percent: 'Oil %',
  drive_thru: 'Drive-thru time (s)', transactions: 'Transactions', discount_percent: 'Discount %', cash_variance: 'Cash over/short', waste_percent: 'Waste %',
};

export default function GoogleSheetDialog({ open, onOpenChange }) {
  const [cfg, setCfg] = useState(null);
  const [url, setUrl] = useState('');
  const [tab, setTab] = useState('');
  const [weekDefault, setWeekDefault] = useState('');
  const [preview, setPreview] = useState(null);
  const [mapping, setMapping] = useState({});
  const [busy, setBusy] = useState('');

  useEffect(() => {
    if (!open) return;
    axios.get(`${API_URL}/integrations/gsheet/config`).then(r => {
      setCfg(r.data);
      if (r.data.configured) { setUrl(r.data.url); setTab(r.data.tab || ''); setMapping(r.data.mapping || {}); setWeekDefault(r.data.week_ending_default || ''); }
    }).catch(() => {});
  }, [open]);

  const doPreview = async () => {
    setBusy('preview');
    try {
      const r = await axios.post(`${API_URL}/integrations/gsheet/preview`, { url, tab: tab || null });
      setPreview(r.data);
      setMapping(m => ({ ...r.data.suggested_mapping, ...m }));
      toast.success(`Read ${r.data.row_count} rows · ${r.data.headers.length} columns`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not read the sheet'); }
    finally { setBusy(''); }
  };

  const save = async () => {
    setBusy('save');
    try {
      const clean = Object.fromEntries(Object.entries(mapping).filter(([, v]) => v));
      await axios.put(`${API_URL}/integrations/gsheet/config`, { url, tab: tab || null, mapping: clean, week_ending_default: weekDefault || null, label: 'Clearview Google Sheet' });
      toast.success('Sheet connected — syncs daily at 6:45 AM');
      setCfg(c => ({ ...c, configured: true }));
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    finally { setBusy(''); }
  };

  const syncNow = async () => {
    setBusy('sync');
    try {
      const r = await axios.post(`${API_URL}/integrations/gsheet/sync-now`);
      r.data.status === 'ok' ? toast.success(r.data.summary) : toast.error(r.data.error || r.data.summary || r.data.status);
    } catch (e) { toast.error(e.response?.data?.detail || 'Sync failed'); }
    finally { setBusy(''); }
  };

  const headers = preview?.headers || (cfg?.mapping ? Object.values(cfg.mapping) : []);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="gsheet-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Table2 className="w-5 h-5 text-green-600" />Clearview Google Sheet feed</DialogTitle></DialogHeader>
        <div className="space-y-4 text-sm">
          <p className="text-slate-500">Share the sheet as <b>Anyone with the link · Viewer</b>, paste the link, preview, check the column mapping, save. The app re-reads it every morning at 6:45 AM (and you can sync any time).</p>
          {cfg?.configured && <p className="text-xs text-slate-500" data-testid="gsheet-last">Last sync: {cfg.last_sync_at ? new Date(cfg.last_sync_at).toLocaleString() : 'never'} · {cfg.last_status || '—'} {cfg.last_summary ? `· ${cfg.last_summary}` : ''}</p>}
          <div className="grid gap-2 sm:grid-cols-[1fr_180px]">
            <Input value={url} onChange={e => setUrl(e.target.value)} placeholder="https://docs.google.com/spreadsheets/d/…" data-testid="gsheet-url" />
            <Input value={tab} onChange={e => setTab(e.target.value)} placeholder="Tab name (optional)" data-testid="gsheet-tab" />
          </div>
          <Button variant="outline" onClick={doPreview} disabled={!url || busy === 'preview'} className="rounded-sm" data-testid="gsheet-preview-btn">
            {busy === 'preview' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}Read sheet
          </Button>

          {headers.length > 0 && (
            <div className="rounded-lg border border-slate-200 divide-y" data-testid="gsheet-mapping">
              {Object.entries(LABELS).map(([target, label]) => (
                <div key={target} className="flex items-center justify-between gap-3 px-3 py-1.5">
                  <span className="text-slate-700">{label}</span>
                  <select value={mapping[target] || ''} onChange={e => setMapping(m => ({ ...m, [target]: e.target.value }))} className="h-8 rounded border border-slate-200 text-xs px-2 w-56 bg-white" data-testid={`gsheet-map-${target}`}>
                    <option value="">— not in sheet —</option>
                    {headers.map(h => <option key={h} value={h}>{h}</option>)}
                  </select>
                </div>
              ))}
              {!mapping.week_ending && (
                <div className="flex items-center justify-between gap-3 px-3 py-1.5 bg-amber-50">
                  <span className="text-amber-800">No week-ending column — use this date for all rows</span>
                  <Input type="date" value={weekDefault} onChange={e => setWeekDefault(e.target.value)} className="h-8 w-56 text-xs" data-testid="gsheet-week-default" />
                </div>
              )}
            </div>
          )}

          {preview && (
            <div className="overflow-x-auto rounded border border-slate-100">
              <table className="text-[11px] min-w-full">
                <thead className="bg-slate-50"><tr>{preview.headers.map(h => <th key={h} className="px-2 py-1 text-left font-semibold whitespace-nowrap">{h}</th>)}</tr></thead>
                <tbody>{preview.sample.slice(0, 3).map((r, i) => <tr key={i} className="border-t">{preview.headers.map(h => <td key={h} className="px-2 py-1 whitespace-nowrap">{r[h]}</td>)}</tr>)}</tbody>
              </table>
            </div>
          )}

          <div className="flex justify-end gap-2">
            {cfg?.configured && <Button variant="outline" onClick={syncNow} disabled={!!busy} className="rounded-sm" data-testid="gsheet-sync-btn">{busy === 'sync' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}Sync now</Button>}
            <Button onClick={save} disabled={!url || headers.length === 0 || !!busy} className="bg-green-600 hover:bg-green-700 text-white rounded-sm" data-testid="gsheet-save-btn">
              {busy === 'save' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}Save & connect
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
